import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { api } from "@/lib/api";
import { dt } from "@/lib/format";
import { buildProxyLink } from "@/lib/proxy-link";
import type { Tier } from "@/lib/types";
import { Button, Card, DarkSelect, Modal, PageHeader, Pill } from "@/components/ui";
import { confirmPopup } from "@/components/popup";
import { ServerForm, type ServerDetails } from "@/components/ServerForm";
import { ConnectionsModal } from "@/components/ConnectionsModal";

interface BridgeHealth {
  reachable: boolean | null;
  active: boolean | null;
  error?: string | null;
}

interface ServerHealth {
  reachable: boolean;
  installed: boolean;
  active: boolean;
  kind: string | null;
  expected_kind: string | null;
  error: string | null;
  bridge?: BridgeHealth | null;
}

interface ServerAction {
  success: boolean;
  error: string | null;
}

interface ServerLogs {
  logs: string;
}

interface HealthPoint {
  ts: string;
  connections: number;
  reachable: boolean;
  active: boolean;
}

interface MetricsResponse {
  points: HealthPoint[];
}

const RANGES = [
  { hours: 1, label: "1ч" },
  { hours: 6, label: "6ч" },
  { hours: 24, label: "24ч" },
  { hours: 24 * 7, label: "7д" },
];

interface TestSecret {
  test_secret: string;
  link: string;
}

interface PublicSecret {
  public_secret: string;
  link: string | null;
}

interface GeoStatus {
  state:
    | "applied"
    | "not_applied"
    | "disabled"
    | "broken"
    | "missing_tools"
    | "stale"
    | "unreachable";
  expected_enabled: boolean;
  expected_countries: string;
  cidrs: number | null;
  rule_present: boolean | null;
  drops: number | null;
  error: string | null;
}

interface GeoApplyResult {
  success: boolean;
  enabled: boolean;
  countries: string | null;
  error: string | null;
  stdout: string | null;
  verified: GeoStatus | null;
  applied_countries: string[];
  failed_countries: string[];
}

// Локальный обёрток над общим buildProxyLink — premium server отдаёт
// `ee+FakeTLS+cloak`, обычный — `dd`. cloakDomain важно передавать
// явно: для premium сервер может иметь свой cloak_domain (override),
// и ссылка должна совпадать с tls_domain в config.toml на exit.
const buildLink = (
  host: string,
  port: number,
  secret: string,
  tier?: Tier,
  cloakDomain?: string | null,
) =>
  buildProxyLink({
    host,
    port,
    secret,
    tier,
    cloakDomain: cloakDomain || undefined,
  });

export default function ServerDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const serverId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [editOpen, setEditOpen] = useState(false);
  const [connectionsOpen, setConnectionsOpen] = useState(false);
  const [logLines, setLogLines] = useState(200);
  const [metricsHours, setMetricsHours] = useState(24);

  const details = useQuery<ServerDetails>({
    queryKey: ["server", serverId],
    queryFn: () => api<ServerDetails>(`/api/admin/servers/${serverId}`),
    enabled: !Number.isNaN(serverId),
  });

  const health = useQuery<ServerHealth>({
    queryKey: ["server-health", serverId],
    queryFn: () => api<ServerHealth>(`/api/admin/servers/${serverId}/health`),
    enabled: !Number.isNaN(serverId),
    refetchInterval: 15000,
  });

  const metrics = useQuery<MetricsResponse>({
    queryKey: ["server-metrics", serverId, metricsHours],
    queryFn: () =>
      api<MetricsResponse>(
        `/api/admin/servers/${serverId}/metrics?hours=${metricsHours}`,
      ),
    enabled: !Number.isNaN(serverId),
    refetchInterval: 30000,
  });

  const logs = useQuery<ServerLogs>({
    queryKey: ["server-logs", serverId, logLines],
    queryFn: () => api<ServerLogs>(`/api/admin/servers/${serverId}/logs?lines=${logLines}`),
    enabled: !Number.isNaN(serverId),
  });

  const restart = useMutation<ServerAction>({
    mutationFn: () =>
      api<ServerAction>(`/api/admin/servers/${serverId}/restart`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["server-health", serverId] });
      qc.invalidateQueries({ queryKey: ["server-logs", serverId] });
    },
  });

  const install = useMutation<ServerAction>({
    mutationFn: () =>
      api<ServerAction>(`/api/admin/servers/${serverId}/install`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["server-health", serverId] });
    },
  });

  const uninstall = useMutation<ServerAction>({
    mutationFn: () =>
      api<ServerAction>(`/api/admin/servers/${serverId}/uninstall`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["server-health", serverId] });
    },
  });

  const wipe = useMutation<ServerAction>({
    mutationFn: () =>
      api<ServerAction>(`/api/admin/servers/${serverId}/wipe`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["server-health", serverId] });
      qc.invalidateQueries({ queryKey: ["server-geo-status", serverId] });
    },
  });

  const remove = useMutation({
    mutationFn: (force: boolean) =>
      api(`/api/admin/servers/${serverId}${force ? "?force=true" : ""}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["servers"] });
      navigate("/servers");
    },
  });

  const toggleActive = useMutation<ServerDetails>({
    mutationFn: () =>
      api<ServerDetails>(`/api/admin/servers/${serverId}/toggle-active`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["server", serverId] });
      qc.invalidateQueries({ queryKey: ["servers"] });
    },
  });

  const [geoFastPolling, setGeoFastPolling] = useState(false);

  const geoStatus = useQuery<GeoStatus>({
    queryKey: ["server-geo-status", serverId],
    queryFn: () => api<GeoStatus>(`/api/admin/servers/${serverId}/geo-firewall/status`),
    enabled: !Number.isNaN(serverId),
    refetchInterval: geoFastPolling ? 8000 : 60000,
  });

  const applyGeo = useMutation<GeoApplyResult>({
    mutationFn: () =>
      api(`/api/admin/servers/${serverId}/geo-firewall`, { method: "POST" }),
    onMutate: () => {
      setGeoFastPolling(true);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["server", serverId] });
      qc.invalidateQueries({ queryKey: ["server-geo-status", serverId] });
      // Stop fast polling after 3 min — by then either applied or it's
      // genuinely broken and the user should see the stable error state.
      setTimeout(() => setGeoFastPolling(false), 180_000);
    },
  });

  const clearGeo = useMutation<ServerAction>({
    mutationFn: () =>
      api<ServerAction>(`/api/admin/servers/${serverId}/geo-clear`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["server", serverId] });
      qc.invalidateQueries({ queryKey: ["server-geo-status", serverId] });
    },
  });

  const regenTest = useMutation<TestSecret>({
    mutationFn: () =>
      api<TestSecret>(`/api/admin/servers/${serverId}/test-secret`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["server", serverId] }),
  });

  const dropTest = useMutation({
    mutationFn: () =>
      api(`/api/admin/servers/${serverId}/test-secret`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["server", serverId] }),
  });

  const regenPublic = useMutation<PublicSecret>({
    mutationFn: () =>
      api<PublicSecret>(`/api/admin/servers/${serverId}/public-secret`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["server", serverId] }),
  });

  if (details.isLoading) return <div className="p-4 md:p-8 text-zinc-400">Загрузка…</div>;
  if (details.isError || !details.data)
    return <div className="p-4 md:p-8 text-red-400">Не нашли сервер</div>;

  const s = details.data;
  const h = health.data;
  const isAd = s.server_type === "ad";
  const pct = s.max_users > 0 ? Math.round((s.active_users / s.max_users) * 100) : 0;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={`Сервер #${s.id} — ${s.name}`}
        right={
          <Link to="/servers" className="text-sm text-zinc-400 hover:text-zinc-200">
            ← к списку
          </Link>
        }
      />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card title="Основное">
          <dl className="text-sm space-y-2.5">
            <Row k="host" v={<span className="font-mono">{s.host}</span>} />
            <Row k="port" v={s.port} />
            <Row
              k="тип"
              v={
                <Pill tone={s.server_type === "paid" ? "success" : "neutral"}>
                  {s.server_type}
                </Pill>
              }
            />
            <Row
              k="тариф"
              v={
                (s.tier ?? "standard") === "premium" ? (
                  <Pill tone="warning">👑 premium</Pill>
                ) : (
                  <Pill tone="neutral">standard</Pill>
                )
              }
            />
            {!isAd && <Row k="max_users" v={s.max_users} />}
            {isAd && <Row k="max_conn/worker" v={s.max_connections.toLocaleString()} />}
            {!isAd && (
              <Row
                k="загрузка"
                v={
                  <span>
                    {s.active_users}/{s.max_users} ({pct}%)
                  </span>
                }
              />
            )}
            <Row
              k="статус"
              v={
                s.is_active ? (
                  <Pill tone="success">активен</Pill>
                ) : (
                  <Pill tone="danger">неактивен</Pill>
                )
              }
            />
            <Row k="ssh" v={`${s.ssh_user ?? "root"}@${s.ssh_host ?? s.host}:${s.ssh_port}`} />
            {s.ad_tag && <Row k="ad_tag" v={<span className="font-mono">{s.ad_tag}</span>} />}
            {s.cluster_id && (
              <Row
                k="кластер"
                v={
                  <Link
                    to={`/clusters/${s.cluster_id}`}
                    className="text-brand hover:underline"
                  >
                    #{s.cluster_id} →
                  </Link>
                }
              />
            )}
            <Row k="создан" v={dt(s.created_at)} />
          </dl>
          <div className="mt-5 flex gap-3 flex-wrap">
            <Button variant="primary" onClick={() => setEditOpen(true)}>
              Редактировать
            </Button>
            <Button
              variant={s.is_active ? "danger" : "success"}
              disabled={toggleActive.isPending}
              onClick={() => toggleActive.mutate()}
            >
              {toggleActive.isPending
                ? "…"
                : s.is_active
                  ? "Отключить выдачу"
                  : "Включить выдачу"}
            </Button>
          </div>
        </Card>

        <Card title="Live-статус">
          <HealthBlock health={h} loading={health.isFetching} />
          <div className="mt-5 flex gap-3 flex-wrap">
            <Button
              variant="primary"
              disabled={restart.isPending}
              onClick={async () => {
                if (
                  await confirmPopup({
                    title: "Перезапустить прокси?",
                    body: `Сервер ${s.name}.`,
                    confirmLabel: "Перезапустить",
                    danger: true,
                  })
                ) {
                  restart.mutate();
                }
              }}
            >
              {restart.isPending ? "Перезапуск…" : "Restart"}
            </Button>
            <Button
              variant="primary"
              disabled={
                install.isPending ||
                (isAd && !s.public_secret && !s.cluster_id)
              }
              title={
                isAd && !s.public_secret && !s.cluster_id
                  ? "Нет public_secret. Задай в карточке или привяжи к кластеру."
                  : undefined
              }
              onClick={async () => {
                const isPremium = (s.tier ?? "standard") === "premium";
                const what = isPremium
                  ? `Установить mtproto.zig${
                      s.bridge_host
                        ? " на exit + xray dokodemo на bridge"
                        : " на exit (без bridge)"
                    }`
                  : isAd
                    ? "Установить docker telegrammessenger/proxy"
                    : "Установить mtprotoproxy (alexbers)";
                if (
                  await confirmPopup({
                    title: "Установить прокси?",
                    body: `${what} на ${s.name}? Займёт до 4 минут.`,
                    confirmLabel: "Установить",
                  })
                ) {
                  install.mutate();
                }
              }}
            >
              {install.isPending ? "Установка…" : "Install"}
            </Button>
            <Button
              variant="danger"
              disabled={uninstall.isPending}
              onClick={async () => {
                if (
                  await confirmPopup({
                    title: "Удалить прокси?",
                    body: `Удалить прокси с ${s.name}? Снесу docker-контейнеры (telegrammessenger/proxy + alexbers/mtprotoproxy), alexbers-сервис, premium zig (mtproto-proxy.service) и watchdog. iptables/geo/sysctl не трогаю. Юзеры отвалятся. После этого сможешь поставить заново через Install.`,
                    confirmLabel: "Удалить",
                    danger: true,
                  })
                ) {
                  uninstall.mutate();
                }
              }}
            >
              {uninstall.isPending ? "Удаляю…" : "Удалить прокси"}
            </Button>
            <Button
              variant="danger"
              disabled={wipe.isPending}
              onClick={async () => {
                if (
                  await confirmPopup({
                    title: "Очистить сервер до голого состояния?",
                    body: `Сервер ${s.name}. В дополнение к «Удалить прокси» снесу: iptables geo-rule, ipset nolags_geo, sysctl-тюнинг (/etc/sysctl.d/99-nolags-mtproxy.conf), кастомный docker daemon.json.\n\nЭтот сервер будет полностью «голый» после операции. Юзеры отвалятся.`,
                    confirmLabel: "Очистить",
                    danger: true,
                  })
                ) {
                  wipe.mutate();
                }
              }}
            >
              {wipe.isPending ? "Очистка…" : "Очистить сервер"}
            </Button>
          </div>
          {uninstall.data && uninstall.data.success && (
            <div className="mt-3 text-xs text-emerald-400">
              Прокси снесён. Жми «Install» чтобы поставить заново.
            </div>
          )}
          {uninstall.data && !uninstall.data.success && (
            <div className="mt-3 text-xs text-red-400">
              Не получилось удалить: {uninstall.data.error ?? "unknown"}
            </div>
          )}
          {wipe.data && wipe.data.success && (
            <div className="mt-3 text-xs text-emerald-400">
              Сервер очищен до голого состояния.
            </div>
          )}
          {wipe.data && !wipe.data.success && (
            <div className="mt-3 text-xs text-red-400">
              Не получилось очистить: {wipe.data.error ?? "unknown"}
            </div>
          )}
          {restart.data && !restart.data.success && (
            <div className="mt-3 text-xs text-red-400">
              Restart failed: {restart.data.error ?? "unknown"}
            </div>
          )}
          {install.data && !install.data.success && (
            <div className="mt-3 text-xs text-red-400">
              Install failed: {install.data.error ?? "unknown"}
            </div>
          )}
          {install.data?.success && (
            <div className="mt-3 text-xs text-emerald-400">Установка прошла успешно.</div>
          )}
        </Card>

        <Card title="Опасная зона">
          <div className="text-sm text-zinc-400 mb-4">
            Удаление сервера безвозвратно. Все доступы пользователей на нём тоже удалятся.
          </div>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={async () => {
              const body =
                s.active_users > 0
                  ? `На сервере ${s.active_users} активных доступов. Всё равно удалить?`
                  : `Сервер #${s.id} будет удалён безвозвратно.`;
              if (
                await confirmPopup({
                  title: "Удалить сервер?",
                  body,
                  confirmLabel: "Удалить",
                  danger: true,
                })
              ) {
                remove.mutate(s.active_users > 0);
              }
            }}
          >
            Удалить сервер
          </Button>
          {remove.isError && (
            <div className="mt-3 text-xs text-red-400">{(remove.error as Error).message}</div>
          )}
        </Card>
      </section>

      {isAd && s.cluster_id && (
        <section className="mb-6">
          <Card title="Публичная ссылка (из кластера)">
            <div className="text-sm text-zinc-400">
              Этот сервер входит в{" "}
              <Link
                to={`/clusters/${s.cluster_id}`}
                className="text-brand hover:underline"
              >
                кластер #{s.cluster_id}
              </Link>
              . Публичный secret и ad_tag берутся оттуда, общая ссылка показана на
              странице кластера.
            </div>
          </Card>
        </section>
      )}

      {isAd && !s.cluster_id && (
        <section className="mb-6">
          <Card title="Публичная рекламная ссылка">
            {s.public_secret ? (
              <>
                <LinkRow link={buildLink(s.host, s.port, s.public_secret, s.tier as Tier | undefined, s.cloak_domain)} />
                <div className="mt-4 flex gap-3 flex-wrap">
                  <Button
                    variant="ghost"
                    disabled={regenPublic.isPending}
                    onClick={async () => {
                      if (
                        await confirmPopup({
                          title: "Сгенерировать новый public_secret?",
                          body: "Старая публичная ссылка перестанет работать у всех, кто её сохранил.",
                          confirmLabel: "Сгенерировать",
                          danger: true,
                        })
                      ) {
                        regenPublic.mutate();
                      }
                    }}
                  >
                    {regenPublic.isPending ? "Генерируем…" : "Пересоздать secret"}
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-3">
                  Эту ссылку вставляешь в @MTProto_nolags / рекламу. Все, кто по ней
                  приходят, подключаются к этому серверу. После замены secret'а надо
                  нажать Install или Restart чтобы docker подхватил новое значение.
                </p>
              </>
            ) : (
              <>
                <div className="text-sm text-zinc-400 mb-3">
                  public_secret не задан. Установите его в редактировании или нажмите
                  «Сгенерировать».
                </div>
                <Button
                  variant="primary"
                  disabled={regenPublic.isPending}
                  onClick={() => regenPublic.mutate()}
                >
                  {regenPublic.isPending ? "Генерируем…" : "Сгенерировать"}
                </Button>
              </>
            )}
          </Card>
        </section>
      )}

      {isAd && !s.cluster_id && s.public_secret && (
        <section className="mb-6">
          <Card title="Регистрация в @MTProxyAdminBot">
            <ol className="text-sm text-zinc-300 space-y-3 list-decimal list-inside">
              <li>
                Открой{" "}
                <a
                  href="https://t.me/MTProxybot"
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand hover:underline"
                >
                  @MTProxybot
                </a>{" "}
                и жми /newproxy.
              </li>
              <li>
                На «Send me its address in format host:port» отправь:
                <div className="mt-2">
                  <CopyRow text={`${s.host}:${s.port}`} />
                </div>
              </li>
              <li>
                На «Now please specify its secret» отправь <b>чистые 32 hex</b> —{" "}
                <span className="text-amber-300">без префикса dd/ee</span>:
                <div className="mt-2">
                  <CopyRow text={s.public_secret} mono />
                </div>
              </li>
              <li>
                Бот вернёт тег и тестовую ссылку. Тег (32 hex) — в поле ad_tag
                этого сервера (через «Редактировать»), потом жми «Install» или
                «Restart» чтобы docker перекатился.
              </li>
            </ol>
            <p className="text-xs text-zinc-500 mt-4">
              `dd` в пользовательской ссылке выше — это клиентский префикс FakeTLS,
              а не часть секрета. @MTProxybot принимает только 32 hex.
            </p>
          </Card>
        </section>
      )}

      {!isAd && (
        <section className="mb-6">
          <Card title="Тестовый admin-прокси">
            {s.test_secret ? (
              <>
                <LinkRow link={buildLink(s.host, s.port, s.test_secret, s.tier as Tier | undefined, s.cloak_domain)} />
                <div className="mt-4 flex gap-3 flex-wrap">
                  <Button
                    variant="ghost"
                    disabled={regenTest.isPending}
                    onClick={() => regenTest.mutate()}
                  >
                    {regenTest.isPending ? "Генерируем…" : "Пересоздать"}
                  </Button>
                  <Button
                    variant="danger"
                    disabled={dropTest.isPending}
                    onClick={async () => {
                      if (
                        await confirmPopup({
                          title: "Удалить тестовый secret с сервера?",
                          confirmLabel: "Удалить",
                          danger: true,
                        })
                      ) {
                        dropTest.mutate();
                      }
                    }}
                  >
                    {dropTest.isPending ? "Удаляем…" : "Удалить"}
                  </Button>
                </div>
                <p className="text-xs text-zinc-500 mt-3">
                  Персональный secret для админ-тестов. Добавлен в alexbers USERS как
                  <code className="mx-1">_admin_test</code>. Можно смело удалять после
                  проверки.
                </p>
              </>
            ) : (
              <>
                <div className="text-sm text-zinc-400 mb-3">
                  Без тестового secret. Можно создать, проверить подключение в Telegram,
                  потом удалить.
                </div>
                <Button
                  variant="primary"
                  disabled={regenTest.isPending}
                  onClick={() => regenTest.mutate()}
                >
                  {regenTest.isPending ? "Создаём…" : "Создать тестовый secret"}
                </Button>
              </>
            )}
          </Card>
        </section>
      )}

      <section className="mb-6">
        <Card title="Нагрузка">
          <LoadBlock
            points={metrics.data?.points ?? []}
            loading={metrics.isFetching && !metrics.data}
            hours={metricsHours}
            onRange={setMetricsHours}
          />
          <div className="mt-4">
            <Button variant="ghost" onClick={() => setConnectionsOpen(true)}>
              Источники коннектов
            </Button>
          </div>
        </Card>
      </section>

      <section className="mb-6">
        <Card title="Гео-фильтр (iptables)">
          <GeoStatusBanner status={geoStatus.data} loading={geoStatus.isFetching && !geoStatus.data} />

          {s.geo_allow_countries ? (
            <div className="text-sm text-zinc-300 mb-2">
              Разрешены страны:{" "}
              <span className="font-mono text-zinc-100">{s.geo_allow_countries}</span>
            </div>
          ) : (
            <div className="text-sm text-zinc-400 mb-4">
              Фильтр <b>не настроен</b>: в БД пустой список стран. Чтобы
              включить — открой «Редактировать» наверху, заполни поле
              «Страны» (например{" "}
              <code>RU,BY,UA,KZ,DE,FR,IT,GB,NL,PL</code>) и сохрани, потом
              вернись сюда и жми «Применить фильтр».
            </div>
          )}
          {s.geo_allow_countries && (
            <div className="text-xs text-zinc-500 mb-4">
              На сервере стоит/должен стоять ipset + iptables DROP для любого
              источника вне списка. Чтобы <b>применить</b> актуальный список —
              нажми кнопку ниже (скачает свежие CIDR с ipdeny.com).
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            {(() => {
              // Action button matrix:
              // - Хочет фильтр (countries set) → «Применить фильтр» (ставит /
              //   ре-ставит / чинит broken)
              // - Хочет без фильтра (countries empty):
              //   - state stale → «Снять фильтр» (зачистить остатки)
              //   - state disabled / missing_tools → action не нужен, кнопка
              //     скрыта (нечего ни ставить, ни снимать)
              const hasIntent = !!s.geo_allow_countries;
              const state = geoStatus.data?.state;
              const showApply = hasIntent;
              const showClear =
                !hasIntent && state === "stale";
              if (!showApply && !showClear) return null;
              return (
                <Button
                  variant="primary"
                  disabled={applyGeo.isPending}
                  onClick={async () => {
                    const opts = showApply
                      ? {
                          title: "Применить гео-фильтр?",
                          body: `На ${s.name} будут дропаться все коннекты не из: ${s.geo_allow_countries}.\n\nПервый запуск ставит ipset+iptables-persistent и качает CIDR'ы — 1-3 мин.`,
                          confirmLabel: "Применить",
                        }
                      : {
                          title: "Снять остатки гео-фильтра?",
                          body: `С ${s.name}. Доступ станет со всего мира.`,
                          confirmLabel: "Снять",
                          danger: true,
                        };
                    if (await confirmPopup(opts)) applyGeo.mutate();
                  }}
                >
                  {applyGeo.isPending
                    ? "Применяем… (до 3 мин)"
                    : showApply
                      ? "Применить фильтр"
                      : "Снять остатки"}
                </Button>
              );
            })()}
            <Button
              variant="ghost"
              disabled={geoStatus.isFetching}
              onClick={() => geoStatus.refetch()}
            >
              {geoStatus.isFetching ? "Проверяю…" : "Проверить статус"}
            </Button>
            {(s.geo_allow_countries ||
              geoStatus.data?.state === "applied" ||
              geoStatus.data?.state === "stale") && (
              <Button
                variant="danger"
                disabled={clearGeo.isPending}
                onClick={async () => {
                  if (
                    await confirmPopup({
                      title: "Очистить гео-фильтр?",
                      body: `С ${s.name}. Удалит iptables-правило и ipset nolags_geo, очистит поле «Страны» в БД (чтобы install не накатил фильтр заново через self-heal). Прокся продолжит работать, но без географической фильтрации.`,
                      confirmLabel: "Очистить",
                      danger: true,
                    })
                  ) {
                    clearGeo.mutate();
                  }
                }}
              >
                {clearGeo.isPending ? "Очищаю…" : "Очистить гео-фильтр"}
              </Button>
            )}
          </div>
          {clearGeo.data && clearGeo.data.success && (
            <div className="mt-4 rounded-lg border border-emerald-900/50 bg-emerald-950/30 p-3">
              <div className="text-sm font-medium text-emerald-300">
                ✓ Гео-фильтр снят, поле «Страны» в БД очищено.
              </div>
            </div>
          )}
          {clearGeo.data && !clearGeo.data.success && (
            <div className="mt-4 rounded-lg border border-red-900/50 bg-red-950/30 p-3">
              <div className="text-sm font-medium text-red-300">
                ✗ Не удалось снять: {clearGeo.data.error ?? "unknown"}
              </div>
            </div>
          )}
          {applyGeo.data && applyGeo.data.success && (
            <div className="mt-4 rounded-lg border border-emerald-900/50 bg-emerald-950/30 p-3">
              <div className="text-sm font-medium text-emerald-300">
                ✓ {applyGeo.data.enabled ? "Фильтр применён и проверен" : "Фильтр снят и проверен"}
              </div>
              {applyGeo.data.applied_countries.length > 0 && (
                <div className="mt-1 text-xs text-emerald-200/80">
                  Загружено стран: <b>{applyGeo.data.applied_countries.length}</b> · {applyGeo.data.applied_countries.join(", ")}
                </div>
              )}
              {applyGeo.data.verified && (
                <div className="mt-1 text-xs text-emerald-200/80">
                  CIDR'ов в наборе: <b>{applyGeo.data.verified.cidrs ?? "—"}</b>
                  {applyGeo.data.verified.drops != null && (
                    <> · дропов накопилось: <b>{applyGeo.data.verified.drops}</b></>
                  )}
                </div>
              )}
              {applyGeo.data.stdout && (
                <pre className="mt-2 text-xs font-mono text-emerald-200/80 whitespace-pre-wrap max-h-60 overflow-auto">
                  {applyGeo.data.stdout}
                </pre>
              )}
            </div>
          )}
          {applyGeo.data && !applyGeo.data.success && applyGeo.data.failed_countries.length > 0 && (
            <div className="mt-4 rounded-lg border border-amber-900/50 bg-amber-950/30 p-3">
              <div className="text-sm font-medium text-amber-300">
                ⚠ Частичный успех — фильтр стоит, но не все страны догрузились с ipdeny.com
              </div>
              <div className="mt-1 text-xs text-amber-200/80">
                Загружено: <b>{applyGeo.data.applied_countries.length}</b> · {applyGeo.data.applied_countries.join(", ")}
              </div>
              <div className="mt-1 text-xs text-red-300">
                Не догрузились ({applyGeo.data.failed_countries.length}): <b>{applyGeo.data.failed_countries.join(", ")}</b>
              </div>
              <div className="mt-2 text-xs text-zinc-400">
                Жми «Применить geo» ещё раз — повторно качаются только эти страны, остальные берутся из кеша на сервере. Если повторяется — у ipdeny.com проблемы, попробуй позже.
              </div>
            </div>
          )}
          {applyGeo.data && !applyGeo.data.success && applyGeo.data.failed_countries.length === 0 && (
            <div className="mt-4 rounded-lg border border-red-900/50 bg-red-950/30 p-3">
              <div className="text-sm font-medium text-red-300">
                ✗ Не применилось — {applyGeo.data.error || "неизвестная ошибка"}
              </div>
              {applyGeo.data.verified && (
                <div className="mt-1 text-xs text-red-200/80">
                  Реальное состояние: <b>{applyGeo.data.verified.state}</b>
                  {applyGeo.data.verified.cidrs != null && <> · CIDR'ов: {applyGeo.data.verified.cidrs}</>}
                  {applyGeo.data.verified.rule_present != null && (
                    <> · iptables-правило: {applyGeo.data.verified.rule_present ? "есть" : "нет"}</>
                  )}
                </div>
              )}
              {applyGeo.data.stdout && (
                <pre className="mt-2 text-xs font-mono text-red-200/80 whitespace-pre-wrap max-h-60 overflow-auto">
                  {applyGeo.data.stdout}
                </pre>
              )}
            </div>
          )}
          {applyGeo.isError && (() => {
            const msg = (applyGeo.error as Error).message ?? "";
            const isTimeout = /\b504\b|gateway time-?out|timeout/i.test(msg);
            if (isTimeout) {
              return (
                <div className="mt-4 rounded-lg border border-amber-900/50 bg-amber-950/30 p-3 text-sm text-amber-200">
                  ⏳ Запрос прервался по таймауту nginx (60с), но скрипт
                  продолжает работу на сервере. Опрашиваю статус каждые 8с —
                  состояние ниже обновится автоматически когда apply завершится.
                </div>
              );
            }
            return (
              <div className="mt-4 rounded-lg border border-red-900/50 bg-red-950/30 p-3 text-sm text-red-300">
                ✗ HTTP error: {msg}
              </div>
            );
          })()}
        </Card>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm uppercase tracking-wider text-zinc-400">
            Логи {isAd ? "(docker logs)" : "mtprotoproxy"}
          </h2>
          <div className="flex items-center gap-3">
            <div className="w-24">
              <DarkSelect
                value={String(logLines)}
                onChange={(v) => setLogLines(Number(v))}
                options={[50, 200, 500, 1000].map((n) => ({ value: String(n), label: String(n) }))}
              />
            </div>
            <Button variant="ghost" onClick={() => logs.refetch()} disabled={logs.isFetching}>
              {logs.isFetching ? "Обновляем…" : "Обновить"}
            </Button>
          </div>
        </div>
        <pre className="rounded-xl border border-zinc-800 bg-black/40 p-4 text-xs font-mono text-zinc-300 overflow-auto max-h-[480px] whitespace-pre-wrap">
          {logs.data?.logs ?? "Загрузка логов…"}
        </pre>
      </section>

      <ConnectionsModal
        serverId={s.id}
        open={connectionsOpen}
        onClose={() => setConnectionsOpen(false)}
      />

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Редактировать сервер #${s.id}`}
      >
        <ServerForm
          mode="edit"
          initial={s}
          onSaved={() => {
            setEditOpen(false);
            qc.invalidateQueries({ queryKey: ["server", s.id] });
            qc.invalidateQueries({ queryKey: ["servers"] });
          }}
          onCancel={() => setEditOpen(false)}
        />
      </Modal>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-6">
      <dt className="text-zinc-500 shrink-0">{k}</dt>
      <dd className="text-zinc-100 text-right">{v}</dd>
    </div>
  );
}

function LinkRow({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-3 items-stretch">
      <div className="flex-1 px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-800 font-mono text-xs text-zinc-100 break-all">
        {link}
      </div>
      <Button
        variant="ghost"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* ignore */
          }
        }}
      >
        {copied ? "Скопировано ✓" : "Копировать"}
      </Button>
    </div>
  );
}

function CopyRow({ text, mono = false }: { text: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-2 items-stretch">
      <div
        className={`flex-1 px-3 py-2 rounded-lg bg-zinc-950 border border-zinc-800 text-xs text-zinc-100 break-all ${
          mono ? "font-mono" : ""
        }`}
      >
        {text}
      </div>
      <button
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            /* ignore */
          }
        }}
        className="h-auto px-3 text-xs font-medium rounded-md border bg-zinc-900 text-zinc-200 border-zinc-800 hover:bg-zinc-700 hover:text-white hover:border-zinc-600 transition-colors"
      >
        {copied ? "✓" : "Копир."}
      </button>
    </div>
  );
}

function LoadBlock({
  points,
  loading,
  hours,
  onRange,
}: {
  points: HealthPoint[];
  loading: boolean;
  hours: number;
  onRange: (h: number) => void;
}) {
  const latest = points.length > 0 ? points[points.length - 1] : null;
  const peak = points.reduce((m, p) => (p.connections > m ? p.connections : m), 0);

  const shortTs = (iso: string) => {
    const d = new Date(iso);
    if (hours <= 24) {
      return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" });
  };

  return (
    <>
      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
            Сейчас
          </div>
          <div className="text-4xl font-semibold text-zinc-100">
            {latest ? latest.connections.toLocaleString("ru-RU") : "—"}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            пик за период: {peak.toLocaleString("ru-RU")}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {RANGES.map((r) => (
            <button
              key={r.hours}
              onClick={() => onRange(r.hours)}
              className={`h-8 px-3 rounded-lg text-sm border transition-colors ${
                hours === r.hours
                  ? "bg-brand-soft text-brand glow-ring border-transparent"
                  : "border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.04]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="h-48 flex items-center justify-center text-zinc-500 text-sm">
          Загружаем метрики…
        </div>
      )}

      {!loading && points.length === 0 && (
        <div className="h-48 flex items-center justify-center text-zinc-600 text-sm">
          Нет данных за выбранный период — poller собирает с интервалом 60с.
        </div>
      )}

      {!loading && points.length > 0 && (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-conn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#34d399" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis
                dataKey="ts"
                tickFormatter={shortTs}
                tick={{ fill: "#71717a", fontSize: 11 }}
                stroke="#27272a"
                minTickGap={40}
              />
              <YAxis
                tick={{ fill: "#71717a", fontSize: 11 }}
                stroke="#27272a"
                width={48}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#18181b",
                  border: "1px solid #3f3f46",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: "#e4e4e7" }}
                labelFormatter={(v) =>
                  new Date(v).toLocaleString("ru-RU", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                }
                formatter={(v) => [Number(v).toLocaleString("ru-RU"), "коннекты"]}
              />
              <Area
                type="monotone"
                dataKey="connections"
                stroke="#34d399"
                strokeWidth={2}
                fill="url(#grad-conn)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </>
  );
}

const KIND_LABEL: Record<string, string> = {
  docker: "docker (C-proxy)",
  alexbers: "alexbers (python)",
  zig: "mtproto.zig",
  mixed: "⚠ и то и то",
  none: "—",
};

function HealthBlock({ health, loading }: { health: ServerHealth | undefined; loading: boolean }) {
  if (!health) {
    return <div className="text-zinc-500 text-sm">Проверяем…</div>;
  }
  const kind = health.kind ?? "none";
  const expected = health.expected_kind ?? null;
  const mismatch =
    kind !== "none" && expected && kind !== expected && kind !== "mixed";
  const hasMixed = kind === "mixed";

  let installedPill: React.ReactNode;
  if (kind === "none") {
    installedPill = <Pill tone="warning">не установлен</Pill>;
  } else if (hasMixed) {
    installedPill = <Pill tone="warning">{KIND_LABEL.mixed}</Pill>;
  } else if (mismatch) {
    installedPill = <Pill tone="warning">{KIND_LABEL[kind]}</Pill>;
  } else {
    installedPill = <Pill tone="success">{KIND_LABEL[kind]}</Pill>;
  }

  return (
    <dl className="text-sm space-y-2.5">
      <Row
        k="ssh"
        v={
          health.reachable ? (
            <Pill tone="success">доступен</Pill>
          ) : (
            <Pill tone="danger">недоступен</Pill>
          )
        }
      />
      <Row k="прокси" v={installedPill} />
      <Row
        k="сервис"
        v={health.active ? <Pill tone="success">active</Pill> : <Pill tone="danger">down</Pill>}
      />
      {mismatch && !hasMixed && (
        <div className="text-xs text-amber-400 mt-2">
          Стоит не тот прокси. Удали через «Удалить прокси» и поставь
          заново через «Install».
        </div>
      )}
      {hasMixed && (
        <div className="text-xs text-amber-400 mt-2">
          На сервере сразу два прокси — мешают друг другу. Удали всё через
          «Удалить прокси», потом «Install».
        </div>
      )}
      {health.error && (
        <div className="text-xs text-red-400 mt-1 break-all">{health.error}</div>
      )}
      {health.bridge && (
        <>
          <div className="border-t border-zinc-800 mt-3 pt-3 text-xs uppercase tracking-wider text-zinc-500">
            Bridge-нода (RU whitelist)
          </div>
          <Row
            k="ssh"
            v={
              health.bridge.reachable === null ? (
                <Pill tone="neutral">не настроен</Pill>
              ) : health.bridge.reachable ? (
                <Pill tone="success">доступен</Pill>
              ) : (
                <Pill tone="danger">недоступен</Pill>
              )
            }
          />
          <Row
            k="xray"
            v={
              health.bridge.active === null ? (
                <Pill tone="neutral">—</Pill>
              ) : health.bridge.active ? (
                <Pill tone="success">active</Pill>
              ) : (
                <Pill tone="danger">down</Pill>
              )
            }
          />
          {health.bridge.error && (
            <div className="text-xs text-red-400 mt-1 break-all">
              {health.bridge.error}
            </div>
          )}
        </>
      )}
      {loading && <div className="text-xs text-zinc-500 mt-1">обновляем…</div>}
    </dl>
  );
}

function geoMeta(status: GeoStatus): {
  tone: "success" | "warning" | "danger" | "neutral";
  text: string;
  hint: string;
} {
  switch (status.state) {
    case "applied":
      return {
        tone: "success",
        text: "Фильтр работает",
        hint: `${status.cidrs} CIDR'ов в списке, дропов накопилось: ${status.drops ?? 0}`,
      };
    case "not_applied":
      return {
        tone: "danger",
        text: "Фильтр НЕ применён",
        hint: "В БД настроены страны, но на сервере ipset/iptables не настроены. Жми «Применить фильтр».",
      };
    case "disabled":
      return {
        tone: "neutral",
        text: "Фильтр отключён",
        hint: "Доступ открыт со всех IP.",
      };
    case "broken":
      if ((status.cidrs ?? 0) > 0 && status.rule_present === false) {
        return {
          tone: "warning",
          text: "iptables-правило слетело",
          hint: `ipset с ${status.cidrs} CIDR'ами на месте, но самое iptables-правило отсутствует (часто после ребута без iptables-persistent). Нажми «Применить фильтр» — переустановит правило, ipset не нужно перекачивать.`,
        };
      }
      if ((status.cidrs ?? 0) === 0 && status.rule_present === true) {
        return {
          tone: "danger",
          text: "Опасное состояние: правило есть, ipset пуст",
          hint: "iptables дропает ВСЁ что не в пустом наборе → реально дропает все коннекты. Срочно «Применить фильтр».",
        };
      }
      return {
        tone: "danger",
        text: "Фильтр в неконсистентном состоянии",
        hint: `ipset: ${status.cidrs} CIDR'ов · iptables-правило: ${status.rule_present ? "есть" : "нет"}. Переприменить.`,
      };
    case "missing_tools":
      return {
        tone: "danger",
        text: "ipset/iptables не установлены",
        hint: "На сервере нет нужных пакетов. «Применить фильтр» поставит их через apt — занимает 1–3 минуты.",
      };
    case "stale":
      return {
        tone: "warning",
        text: "Остатки старого фильтра",
        hint: "В БД фильтр выключен, но на сервере ещё висят правила. Жми «Снять фильтр», чтобы зачистить.",
      };
    case "unreachable":
      return {
        tone: "warning",
        text: "SSH недоступен",
        hint: status.error ?? "",
      };
    default:
      return { tone: "neutral", text: String(status.state), hint: "" };
  }
}

function GeoStatusBanner({ status, loading }: { status: GeoStatus | undefined; loading: boolean }) {
  if (!status && loading) {
    return <div className="mb-3 text-xs text-zinc-500">Проверяю состояние на сервере…</div>;
  }
  if (!status) return null;

  const meta = geoMeta(status);
  const borderColor = {
    success: "border-emerald-900/50 bg-emerald-950/30 text-emerald-200",
    warning: "border-amber-900/50 bg-amber-950/30 text-amber-200",
    danger: "border-red-900/50 bg-red-950/30 text-red-200",
    neutral: "border-zinc-800 bg-zinc-950/40 text-zinc-300",
  }[meta.tone];

  return (
    <div className={`mb-4 rounded-lg border p-3 ${borderColor}`}>
      <div className="text-sm font-medium">{meta.text}</div>
      {meta.hint && <div className="mt-1 text-xs opacity-80">{meta.hint}</div>}
    </div>
  );
}
